import {
  ApprovalOperation,
  ApprovalRules,
  ApprovalStatus,
} from 'ui-product-approvals-common';
import type {
  ApprovalData,
  ApprovalEntity,
  BulkApprovalsData,
} from 'ui-product-approvals-common';

import { ACTOR } from '../constants/actors';
import {
  APPROVAL_RULES_ALL_APPROVERS,
  APPROVAL_RULES_ANY_APPROVER,
} from '../constants/trackingConstants';
import type { TrackInteractionExtra } from './usageTracker';

export type NoteOperation = Extract<
  ApprovalOperation,
  'APPROVE' | 'REJECT' | 'REQUEST_CHANGES'
>;

export const isNoteOperation = (
  operation: ApprovalOperation | null
): operation is NoteOperation =>
  operation === ApprovalOperation.APPROVE ||
  operation === ApprovalOperation.REJECT ||
  operation === ApprovalOperation.REQUEST_CHANGES;

interface SubmitNoteOperationArgs {
  api: ApprovalEntity['api'];
  operation: NoteOperation;
  stepId: number | null;
  note: string;
}

export const submitNoteOperation = ({
  api,
  operation,
  stepId,
  note,
}: SubmitNoteOperationArgs) => {
  switch (operation) {
    case ApprovalOperation.APPROVE:
      return stepId != null ? api.approveStep(stepId, note) : api.approve(note);
    case ApprovalOperation.REJECT:
      return stepId != null ? api.rejectStep(stepId, note) : api.reject(note);
    case ApprovalOperation.REQUEST_CHANGES:
      return stepId != null
        ? api.requestChangesStep(stepId, note)
        : api.requestChanges(note);
    default:
      return operation satisfies never;
  }
};

export const getNoteOperationTrackingExtra = (
  approval: ApprovalEntity,
  operation: NoteOperation,
  data: ApprovalData | BulkApprovalsData | null
): TrackInteractionExtra => {
  const { currentStepId, currentUser } = approval.meta;
  const approverStep = approval.data?.approvalSteps?.find((step) =>
    currentStepId != null
      ? step.id === currentStepId
      : step.approver.actor.actorType === ACTOR.USER &&
        step.approver.actor.id === String(currentUser?.user_id)
  );
  const extra: TrackInteractionExtra = {
    userType: approverStep?.approver.actor.actorType,
  };
  if (
    operation === ApprovalOperation.APPROVE &&
    data &&
    'approvalStatus' in data &&
    data.approvalStatus === ApprovalStatus.APPROVED
  ) {
    extra.complete = true;
  }
  const approvalRules = approval.data?.approvalRules;
  if (approvalRules === ApprovalRules.ANY_CAN_APPROVE) {
    extra.rules = APPROVAL_RULES_ANY_APPROVER;
  } else if (approvalRules === ApprovalRules.NON_SEQUENTIAL) {
    extra.rules = APPROVAL_RULES_ALL_APPROVERS;
  }
  return extra;
};
